import type { ApiError, ApiSuccess } from '@visualry/shared';

// Relative URLs in dev so requests go through the Vite proxy at /api.
// This keeps cookies scoped to the web origin (http://localhost:5173 in dev)
// and avoids CORS for the session cookie. In production the Vite proxy
// is replaced by the Worker's same-origin routing.
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

type ApiRequestInit = Omit<RequestInit, 'body'> & { body?: unknown };

export class ApiClientError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const buildUrl = (path: string, query?: Record<string, string | number | undefined | null>) => {
  // If caller already passed an absolute URL, use it as-is.
  // Otherwise, build against the configured base URL. Empty baseURL
  // means "same origin" (so the Vite proxy in dev or the Worker route
  // in production takes over).
  const base = API_BASE_URL;
  const url = new URL(path.startsWith('http') ? path : `${base}${path}`, base || globalThis.location?.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
};

export const api = async <T>(path: string, init: ApiRequestInit = {}): Promise<T> => {
  const { body, headers, ...rest } = init;
  const response = await fetch(buildUrl(path), {
    ...rest,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let payload: ApiSuccess<T> | ApiError;
  try {
    payload = (await response.json()) as ApiSuccess<T> | ApiError;
  } catch {
    throw new ApiClientError('INTERNAL_ERROR', 'Invalid response from server.', response.status);
  }

  if (!response.ok || payload.success === false) {
    const err = payload.success === false ? payload.error : undefined;
    throw new ApiClientError(
      err?.code ?? 'INTERNAL_ERROR',
      err?.message ?? 'Request failed.',
      response.status,
      err?.details,
    );
  }
  return payload.data;
};

export const apiUrl = buildUrl;
